/**
 * A bounded retry ladder for broker auto-resume (2026-09-13).
 *
 * `request(run)` schedules `run` after the next rung's delay and moves the ladder up one rung; while
 * an attempt is scheduled further requests are `'pending'` (one attempt at a time), and once every
 * rung is used they are `'exhausted'` — the caller latches auto-resume off until the user Connects.
 * The ladder resets to the first rung only after a logon has stayed healthy for `stableAfterMs`
 * (`markHealthy()` starts that clock, `markUnhealthy()` cancels it) or on an explicit `reset()`,
 * so a logon that is accepted and refused 250 ms later (the prod 2026-09-12 `rp_code 13` twin) does
 * not re-arm a fast retry.
 */
export type BackoffSlot = { attempt: number; delayMs: number } | 'pending' | 'exhausted';

export class ResumeBackoff {
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly ladderMs: readonly number[],
    private readonly stableAfterMs: number,
  ) {}

  /** Attempts scheduled since the last reset. */
  get attempts(): number { return this.attempt; }

  /** Whether an attempt is currently scheduled and not yet run. */
  get isPending(): boolean { return this.timer !== null; }

  /** Schedule `run` after the next rung's delay, or say why not. */
  request(run: () => void): BackoffSlot {
    if (this.timer !== null) return 'pending';
    const delayMs = this.ladderMs[this.attempt];
    if (delayMs === undefined) return 'exhausted';
    this.attempt += 1;
    this.timer = setTimeout(() => { this.timer = null; run(); }, delayMs);
    return { attempt: this.attempt, delayMs };
  }

  /** A logon was accepted: if it stays up for `stableAfterMs`, the ladder starts over from rung one. */
  markHealthy(): void {
    if (this.stableTimer !== null) return;
    this.stableTimer = setTimeout(() => { this.stableTimer = null; this.attempt = 0; }, this.stableAfterMs);
  }

  /** The logon/socket went away: a not-yet-stable session does not reset the ladder. */
  markUnhealthy(): void {
    if (this.stableTimer !== null) { clearTimeout(this.stableTimer); this.stableTimer = null; }
  }

  /** Explicit user action: drop any scheduled attempt and start over from rung one. */
  reset(): void {
    this.markUnhealthy();
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
    this.attempt = 0;
  }
}
